import { showToast } from '../components/Common/Toast';
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../contexts/UserContext';
import { isAdmin, ROLES } from '../utils/roleUtils';
import ModernPageHeader from '../components/Common/ModernPageHeader';
import OperationalMetricStrip from '../components/ui/OperationalMetricStrip';
import SortableHeader from '../components/ui/SortableHeader';
import '../styles/modern-enhancements.css';
import TimesheetFormModal from '../components/TimesheetFormModal';
import PTORequestModal from '../components/PTORequestModal';
import PTOEditModal from '../components/PTOEditModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { useConfirmation } from '../hooks/useConfirmation';
import { supabase } from '../utils/supabaseClient';
import { timeOffDays } from '../utils/timeOffValidation';
import { getSchedulingSettings } from '../utils/smartScheduling';
import TimesheetEditHistoryService from '../services/TimesheetEditHistoryService';
import { TIME_TYPE_CONFIG } from '../constants/timeTypes';
import { fmtDate, fmtTime, fmtDateOnly, DATE_NUMERIC, companyDateKey, companyWallClockToUtc } from '../utils/dateFormatters';
import {
  useTimesheets,
  useTimesheetEmployees,
  useTimesheetJobs,
  useTimesheetPTORequests
} from '../hooks/useTimesheetsPageData';

import {
  ClockIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  CalendarDaysIcon,
  UserIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentCheckIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const Timesheets = ({ initialTab, showAdminTabs = false, scopeToSelf = false }) => {
  // useNavigate dropped 2026-08-29 with the 'Active Team' tile, its only caller — this
  // page no longer sends the user anywhere else.
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { modal: confirmDialog, confirm } = useConfirmation();

  // Filter state variables - MUST be declared BEFORE useTimesheets hook
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [overtimeFilter, setOvertimeFilter] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: '', end: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [quickDateFilter, setQuickDateFilter] = useState('all');
  // #5961: column sort, like every other list in the app. null = the loaded order (newest first).
  const [tsSort, setTsSort] = useState(null);
  const handleTsSort = (key) => setTsSort((prev) => (
    prev?.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: ['date', 'total_hours', 'overtime_hours'].includes(key) ? 'desc' : 'asc' }));
  const [selectedIds, setSelectedIds] = useState(new Set());

  // React Query hooks for data fetching
  const { data: employeesData, isLoading: employeesLoading } = useTimesheetEmployees(
    user?.company_id,
    user?.id
  );
  const employees = employeesData?.employees || [];
  const currentEmployee = employeesData?.currentEmployee || null;

  // isAdmin(user) MUST be passed: the hook's third argument decides whether the
  // job list is scoped to the caller. Omitting it made every user - owner
  // included - fall into the assigned-jobs-only branch.
  const { data: jobsData, isLoading: jobsLoading } = useTimesheetJobs(
    user?.company_id,
    user?.id,
    isAdmin(user)
  );
  const jobs = jobsData || [];

  // Determine employee ID for timesheets query (for employee role)
  const employeeIdForTimesheets = React.useMemo(() => {
    // An employee sees their own rows whatever the filter says — this one is a permission,
    // not a preference, so it stays ahead of the picker.
    if (user?.role?.toLowerCase() === 'employee' && currentEmployee) {
      return currentEmployee.id;
    }
    // ...and everyone else gets the employee they picked. This line is what makes the picker
    // DO something: employeeFilter was written by the <select> and read by nothing, so the
    // query stayed company-wide and choosing a name changed the dropdown and not the table.
    return employeeFilter !== 'all' ? employeeFilter : null;
  }, [user?.role, currentEmployee, employeeFilter]);

  // /my/time is the employee's OWN timesheet, but it renders this page, which is shared with
  // the admin Time Tracking screen where company-wide is the right default. Without this an
  // owner opening "Hours This Week" from My Dashboard landed on the whole team's entries
  // (board #514). Seeded in an effect because currentEmployee arrives with the query.
  useEffect(() => {
    if (scopeToSelf && currentEmployee?.id) {
      setEmployeeFilter(currentEmployee.id);
    } else if (scopeToSelf && employeesData && !currentEmployee) {
      // No employee record for this login: "my time" is nothing, not the whole team's 364.75h
      // (audit 2026-10-17). The nil uuid matches no row.
      setEmployeeFilter('00000000-0000-0000-0000-000000000000');
    }
  }, [scopeToSelf, currentEmployee?.id, employeesData]);

  const { data: timesheetsData, isLoading: timesheetsLoading, refetch: refetchTimesheets } = useTimesheets(
    user?.company_id,
    {
      employeeId: employeeIdForTimesheets,
      workOrderId: jobFilter,
      // Board #457: "Logged" covers `submitted` AND the legacy `approved`, because both now
      // render as Logged. The query does an exact status match, so it is asked for everything
      // and the one predicate lives in getFilteredTimesheets below — a badge saying Logged
      // while the Logged filter hid the row is the mismatch class fixed in #22/#171/#87.
      status: statusFilter === 'logged' ? 'all' : statusFilter,
      dateStart: dateRangeFilter.start,
      dateEnd: dateRangeFilter.end,
      overtimeOnly: overtimeFilter,
      searchTerm: searchTerm,
      employees: employees
    }
  );
  const timesheets = timesheetsData || [];
  const loading = timesheetsLoading || employeesLoading || jobsLoading;

  const { data: ptoRequestsData, isLoading: ptoLoading } = useTimesheetPTORequests(
    user?.company_id,
    currentEmployee?.id
  );
  const ptoRequests = ptoRequestsData || [];
  // #5580: the DAYS column is computed with the same rule the insert uses (company open days,
  // timeOffDays). It read the hours_requested stored at insert, so rows written before
  // eba5b6bb7 kept the old weekday-only count and two identical spans showed 1 and 2 days.
  const [companyBusinessHours, setCompanyBusinessHours] = useState(undefined);
  useEffect(() => {
    if (!user?.company_id) return undefined;
    let cancelled = false;
    getSchedulingSettings(user.company_id)
      .then(({ business_hours: bh }) => { if (!cancelled) setCompanyBusinessHours(bh || null); })
      .catch(() => { if (!cancelled) setCompanyBusinessHours(null); });
    return () => { cancelled = true; };
  }, [user?.company_id]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [activeTab, setActiveTab] = useState(initialTab || 'timesheets');

  // PTO functionality - moved from Employees page
  const [showPTOModal, setShowPTOModal] = useState(false);
  const [showPTEEditModal, setShowPTEEditModal] = useState(false);
  const [selectedPTEEdit, setSelectedPTEEdit] = useState(null);


  const calculateTotalHours = (clockIn, clockOut, breakMinutes = 0) => {
    if (!clockIn || !clockOut) return 0;

    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    const breakHours = breakMinutes / 60;

    return Math.max(0, diffHours - breakHours);
  };

  // NEW: Calculate duration in minutes for Phase 1 audit trail
  const calculateDuration = (clockIn, clockOut, breakMinutes = 0) => {
    if (!clockIn || !clockOut) return 0;

    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const durationMs = end - start;
    const durationMinutes = Math.round(durationMs / (1000 * 60));

    return Math.max(0, durationMinutes - (breakMinutes || 0));
  };

  // Quick date filter function
  const applyQuickDateFilter = (filterType) => {
    // Every range is built from the COMPANY day as a 'YYYY-MM-DD' key and shifted with UTC
    // arithmetic on that key. The old version built browser-local midnights and then took
    // toISOString() — a UTC date — so every range except Today (#4900) slid a day for any
    // browser east of UTC, or after 8pm in the company's zone. Weeks still start on Sunday.
    const DAY_MS = 24 * 60 * 60 * 1000;
    const todayKey = companyDateKey();
    const [ty, tm, td] = todayKey.split('-').map(Number);
    const todayMs = Date.UTC(ty, tm - 1, td);
    const dow = new Date(todayMs).getUTCDay();
    const keyAt = (ms) => {
      const t = new Date(ms);
      return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
    };

    switch (filterType) {
      case 'today':
        setDateRangeFilter({ start: todayKey, end: todayKey });
        break;
      case 'yesterday':
        setDateRangeFilter({ start: keyAt(todayMs - DAY_MS), end: keyAt(todayMs - DAY_MS) });
        break;
      case 'thisWeek':
        setDateRangeFilter({ start: keyAt(todayMs - dow * DAY_MS), end: todayKey });
        break;
      case 'lastWeek':
        setDateRangeFilter({ start: keyAt(todayMs - (dow + 7) * DAY_MS), end: keyAt(todayMs - (dow + 1) * DAY_MS) });
        break;
      case 'thisMonth':
        setDateRangeFilter({ start: `${todayKey.slice(0, 8)}01`, end: todayKey });
        break;
      case 'all':
      default:
        setDateRangeFilter({ start: '', end: '' });
        break;
    }
    setQuickDateFilter(filterType);
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    const filteredTimesheets = getFilteredTimesheets();
    if (selectedIds.size === filteredTimesheets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTimesheets.map(t => t.id)));
    }
  };

  const toggleRow = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const bulkApprove = async () => {
    if (selectedIds.size === 0) return;

    try {
      const updates = Array.from(selectedIds).map(id => ({
        id,
        status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by: user.id
      }));

      // SECURITY: Use Supabase client for bulk updates
      for (const update of updates) {
        const { error } = await supabase
          .from('employee_timesheets')
          .update({
            status: update.status,
            approved_at: update.approved_at,
            approved_by: update.approved_by
          })
          .eq('id', update.id);

        if (error) {
          console.error('Error updating timesheet:', error);
          throw error;
        }
      }

      setAlert({ show: true, type: 'success', message: `${selectedIds.size} timesheets approved successfully!` });
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    } catch (error) {
      setAlert({ show: true, type: 'error', message: 'Failed to approve time entries' });
    }
  };

  const bulkReject = async () => {
    if (selectedIds.size === 0) return;

    const reason = prompt('Enter rejection reason (optional):') || 'Bulk rejection';

    try {
      // FIXED: Include rejection reason in the update
      const updates = Array.from(selectedIds).map(id => ({
        id,
        status: 'rejected',
        edit_reason: reason,
        approved_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      // SECURITY: Use Supabase client for bulk denial updates
      for (const update of updates) {
        const { error } = await supabase
          .from('employee_timesheets')
          .update({
            status: update.status,
            edit_reason: update.edit_reason,
            approved_at: update.approved_at,
            updated_at: update.updated_at
          })
          .eq('id', update.id);

        if (error) {
          console.error('Error updating timesheet:', error);
          throw error;
        }
      }

      setAlert({ show: true, type: 'success', message: `${selectedIds.size} timesheets rejected successfully!` });
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    } catch (error) {
      setAlert({ show: true, type: 'error', message: 'Failed to reject time entries' });
    }
  };

  // Smart Export - Export exactly what you see!
  // #2626: the exports wrote the raw status ('submitted') while the table badge says 'Logged'.
  // Same labels as getStatusBadge below.
  const exportStatusLabel = (status) => ({
    draft: 'Draft', submitted: 'Logged', approved: 'Logged', rejected: 'Rejected (legacy)'
  }[status] || 'Draft');

  // #2873: every export was named timesheets_<UTC date>, so a filtered export looked exactly
  // like an all-time one. Company-timezone date, plus the row count and "filtered" when any
  // filter narrowed the list.
  const exportFileName = (rows, ext) => {
    const filtered = employeeFilter !== 'all' || jobFilter !== 'all' || statusFilter !== 'all'
      || overtimeFilter || searchTerm || quickDateFilter !== 'all'
      || dateRangeFilter.start || dateRangeFilter.end;
    return `timesheets_${companyDateKey()}${filtered ? '_filtered' : ''}_${rows.length}-rows.${ext}`;
  };

  const exportTimesheetsCSV = (timesheetsToExport) => {
    const headers = [
      'Employee',
      'Job',
      'Work Date',
      'Clock In',
      'Clock Out',
      'Total Hours',
      'Regular Hours',
      'Overtime Hours',
      // Board #1944. This header had NO corresponding value in the row below, so the CSV was
      // 11 headers against 10 cells and everything from here on shifted one column left:
      // Status printed under "Lunch Taken", Notes under "Status", and the Notes column came out
      // empty. The column is real (employee_timesheets.break_duration) and is now emitted.
      // Renamed to match the unit it actually holds, and to match the wording the other export
      // in this file already uses for the same fact.
      'Break (min)',
      'Status',
      'Notes'
    ];

    const csvData = timesheetsToExport.map(timesheet => {
      const totalHours = timesheet.total_hours || 0; // ✅ BACKEND RPC: Use backend-calculated value
      // #4902: recurring-spawned visits have a work_order_id but no work_order_number.
      const jobDisplay = timesheet.work_order_number
        ? `#${timesheet.work_order_number} – ${timesheet.job_title || 'Untitled'}`
        : (timesheet.work_order_id ? (timesheet.job_title || 'Job') : 'No Job');
      return [
        timesheet.full_name || 'Unknown',
        jobDisplay,
        fmtDateOnly(timesheet.date, DATE_NUMERIC),
        formatTime(timesheet.clock_in),
        formatTime(timesheet.clock_out) + (endsNextDay(timesheet) ? ' (+1 day)' : ''),
        totalHours.toFixed(2),
        timesheet.regular_hours?.toFixed(2) || totalHours.toFixed(2),
        timesheet.overtime_hours?.toFixed(2) || '0.00',
        timesheet.break_duration ?? 0,
        exportStatusLabel(timesheet.status),
        timesheet.notes || ''
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    // #3346: UTF-8 BOM so Excel decodes the en dash in job labels instead of showing mojibake.
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', exportFileName(timesheetsToExport, 'csv'));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Advanced export with multiple formats - Phase 2
  const exportTimesheetsJSON = (timesheetsToExport) => {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalRecords: timesheetsToExport.length,
      summary: calculateSummaryStats(timesheetsToExport),
      timesheets: timesheetsToExport.map(timesheet => ({
        id: timesheet.id,
        employee: {
          id: timesheet.employee_id,
          name: timesheet.employees?.full_name || 'Unknown'
        },
        job: {
          id: timesheet.work_order_id,
          title: timesheet.job_title || 'No Job'
        },
        workDate: timesheet.date,
        clockIn: timesheet.clock_in,
        clockOut: timesheet.clock_out,
        totalHours: timesheet.total_hours || 0, // ✅ BACKEND RPC: Use backend-calculated value
        regularHours: timesheet.regular_hours || 0,
        overtimeHours: timesheet.overtime_hours || 0,
        breakDuration: timesheet.break_duration || 0,
        status: timesheet.status || 'approved',
        notes: timesheet.notes || '',
        createdAt: timesheet.created_at,
        updatedAt: timesheet.updated_at
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', exportFileName(timesheetsToExport, 'json'));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Export timesheets as CSV - matches what user is viewing
  const exportTimesheets = (timesheetsToExport) => {
    if (!timesheetsToExport || timesheetsToExport.length === 0) {
      showToast('warning', 'No time entries to export');
      return;
    }

    const headers = [
      'Date',
      'Employee',
      'Job',
      'Clock In',
      'Clock Out',
      'Break (min)',
      'Total Hours',
      'Regular Hours',
      'Overtime Hours',
      'Status',
      'Notes'
    ];

    const csvRows = timesheetsToExport.map(timesheet => [
      timesheet.date || '',
      timesheet.full_name || 'Unknown',
      timesheet.job_title || (timesheet.work_order_id ? 'Job' : 'No Job'),
      timesheet.clock_in || '',
      timesheet.clock_out || '',
      timesheet.break_duration || 0,
      (timesheet.total_hours || 0).toFixed(2), // ✅ BACKEND RPC: Use backend-calculated value
      (timesheet.regular_hours || 0).toFixed(2),
      (timesheet.overtime_hours || 0).toFixed(2),
      exportStatusLabel(timesheet.status),
      (timesheet.notes || '').replace(/\n/g, ' ').replace(/,/g, ';') // Clean notes for CSV
    ]);

    const csv = [
      headers.join(','),
      ...csvRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }); // #3346 BOM
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', exportFileName(timesheetsToExport, 'csv'));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast('success', `Exported ${timesheetsToExport.length} timesheets to CSV`);
  };

  // ✅ BACKEND RPC: Calculate summary statistics using backend-calculated values
  const calculateSummaryStats = (timesheetsToAnalyze) => {
    const stats = {
      totalHours: 0,
      totalOvertimeHours: 0,
      uniqueEmployees: new Set(),
      loggedEntries: 0
    };

    timesheetsToAnalyze.forEach(timesheet => {
      // Use backend-calculated hours
      stats.totalHours += (timesheet.total_hours || 0);
      stats.totalOvertimeHours += (timesheet.overtime_hours || 0);

      // Track unique employees
      if (timesheet.employee_id) {
        stats.uniqueEmployees.add(timesheet.employee_id);
      }

      // #6164: there is no timesheet approval (PRODUCT_DECISIONS: "NO TIMESHEET APPROVAL"), so
      // this is not "pending approvals". It counts what the page shows as Logged, with the
      // same labels the exports use (exportStatusLabel).
      if (exportStatusLabel(timesheet.status) === 'Logged') {
        stats.loggedEntries++;
      }
    });

    return {
      totalHours: stats.totalHours,
      totalOvertimeHours: stats.totalOvertimeHours,
      uniqueEmployees: stats.uniqueEmployees.size,
      loggedEntries: stats.loggedEntries
    };
  };

  // Load PTO requests - moved from Employees page
  ;

  // Submit PTO request - moved from Employees page
  const handlePTOSubmit = async (ptoData) => {
    try {
      // Use employee ID (not auth user ID) for PTO insert
      const employeeId = currentEmployee?.id;
      const companyId = currentEmployee?.company_id || user.company_id;
      
      if (!employeeId) {
        throw new Error('Employee record not found. Please contact your administrator.');
      }

      // #5321: ONE day-count rule shared with TimeOffPanel — company open days (business_hours).
      const { business_hours: businessHours } = await getSchedulingSettings(companyId);
      const hoursRequested = timeOffDays(
        ptoData.startsAt || ptoData.starts_at,
        ptoData.endsAt || ptoData.ends_at,
        { businessHours }
      ) * 8;

      const { error } = await supabase
        .from('employee_time_off')
        .insert({
          employee_id: employeeId,
          company_id: companyId,
          kind: ptoData.kind,
          starts_at: ptoData.startsAt || ptoData.starts_at,
          ends_at: ptoData.endsAt || ptoData.ends_at,
          hours_requested: hoursRequested,
          note: ptoData.note,
          status: 'PENDING'
        });

      if (!error) {
        showToast('success', 'Time off added — those dates are now blocked for scheduling.');
        queryClient.invalidateQueries({ queryKey: ["timesheet-pto"] });
      } else {
        console.error('PTO insert error:', error);
        throw new Error('Failed to submit PTO request: ' + error.message);
      }
    } catch (error) {
      console.error('Error submitting PTO request:', error);
      showToast('error', 'Failed to add time off');
    }
  };

  // Open PTO edit modal with request data
  const openPTEEditModal = (pto) => {
    setSelectedPTEEdit(pto);
    setShowPTEEditModal(true);
  };

  // Handle PTO update
  const handlePTOUpdate = async (updateData) => {
    try {
      if (!selectedPTEEdit) return;

      // #5321: same shared day-count rule as create (company open days, 8h each).
      const { business_hours: businessHours } = await getSchedulingSettings(user.company_id);
      const hoursRequested = timeOffDays(
        updateData.startsAt || updateData.starts_at,
        updateData.endsAt || updateData.ends_at,
        { businessHours }
      ) * 8;

      const { error } = await supabase
        .from('employee_time_off')
        .update({
          kind: updateData.kind,
          starts_at: updateData.startsAt || updateData.starts_at,
          ends_at: updateData.endsAt || updateData.ends_at,
          hours_requested: hoursRequested,
          note: updateData.note,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPTEEdit.id);

      if (!error) {
        showToast('success', 'Time off updated.');
        queryClient.invalidateQueries({ queryKey: ["timesheet-pto"] });
        setShowPTEEditModal(false);
        setSelectedPTEEdit(null);
      } else {
        throw new Error('Failed to update PTO request: ' + error.message);
      }
    } catch (error) {
      console.error('Error updating PTO request:', error);
      showToast('error', 'Failed to update time off');
    }
  };

  // Handle PTO delete
  const handlePTODelete = async (ptoId) => {
    const ok = await confirm({
      title: 'Delete time off?',
      message: 'Those dates become available for scheduling again.',
      confirmText: 'Delete',
      isDangerous: true
    });
    if (ok !== 'confirm') return;

    try {
      const { error } = await supabase
        .from('employee_time_off')
        .delete()
        .eq('id', ptoId);

      if (!error) {
        showToast('success', 'Time off deleted.');
        queryClient.invalidateQueries({ queryKey: ["timesheet-pto"] });
      } else {
        throw new Error('Failed to delete PTO request: ' + error.message);
      }
    } catch (error) {
      console.error('Error deleting PTO request:', error);
      showToast('error', 'Failed to delete time off');
    }
  };

  // Advanced notification system - Phase 2
  const getNotificationCounts = () => {
    const userTimesheets = timesheets.filter(t => t.user_id === user.id);
    return {
      approved: userTimesheets.filter(t => t.status === 'approved' &&
        t.approved_at && new Date(t.approved_at) > new Date(Date.now() - 7*24*60*60*1000)).length,
      rejected: userTimesheets.filter(t => t.status === 'rejected' &&
        t.approved_at && new Date(t.approved_at) > new Date(Date.now() - 7*24*60*60*1000)).length,
      submitted: timesheets.filter(t => t.status === 'submitted').length
    };
  };

  const getStatusNotification = (timesheet) => {
    if (timesheet.user_id !== user.id) return null;

    const sevenDaysAgo = new Date(Date.now() - 7*24*60*60*1000);

    if (timesheet.status === 'approved' && timesheet.approved_at && new Date(timesheet.approved_at) > sevenDaysAgo) {
      return { type: 'approved', icon: '✅', message: 'Recently approved' };
    }

    if (timesheet.status === 'rejected' && timesheet.approved_at && new Date(timesheet.approved_at) > sevenDaysAgo) {
      return { type: 'rejected', icon: '❌', message: 'Recently rejected' };
    }

    return null;
  };

  // #5836: nothing stopped two entries for the same person covering the same hours — four
  // overlapping shifts on one day, ~32h, live 2026-10-17. Warn and ask, as Jobber/HCP do; a
  // deliberate split entry stays possible. Rejected entries do not count.
  const confirmNoOverlap = async (employeeId, clockInIso, clockOutIso, excludeId = null) => {
    if (!employeeId || !clockInIso || !clockOutIso) return true;
    let q = supabase
      .from('employee_timesheets')
      .select('id, clock_in, clock_out')
      .eq('company_id', user.company_id)
      .eq('employee_id', employeeId)
      .neq('status', 'rejected')
      .lt('clock_in', clockOutIso)
      .gt('clock_out', clockInIso)
      .limit(1);
    if (excludeId) q = q.neq('id', excludeId);
    const { data, error } = await q;
    if (error || !data || data.length === 0) return true;
    const other = data[0];
    const ok = await confirm({
      title: 'This overlaps another time entry',
      message: `This person already has time logged on ${fmtDate(other.clock_in)} from ${fmtTime(other.clock_in)} to ${fmtTime(other.clock_out)}. Saving both counts those hours twice. Save anyway?`,
      confirmText: 'Save anyway'
    });
    return ok === 'confirm';
  };

  const handleCreateTimesheet = async (formData, submitForApproval = false) => {
    try {
      // FIXED: RLS policy requires employee_id to be an actual employee record ID
      // that links to the auth user via employees.user_id = users.auth_user_id = auth.uid()
      // Board #899. Both timestamps were built from the SAME work_date, so a shift ending
      // earlier than it starts — 22:00 to 06:00, an ordinary overnight call-out — persisted a
      // clock_out BEFORE its clock_in. The modal's own preview already treats that as the
      // next day (calculateTimesheetHours wraps), so the hours shown and the timestamps
      // stored disagreed, and whatever the database made of the reversed pair surfaced as a
      // generic "Failed to create time entry". The date has to wrap with the time.
      const clockInFull = `${formData.work_date}T${formData.clock_in}:00`;
      const clockOutDay = (() => {
        if (formData.clock_out > formData.clock_in) return formData.work_date;
        const d = new Date(`${formData.work_date}T00:00:00`);
        d.setDate(d.getDate() + 1);
        // Local parts, not toISOString — this is a calendar day, and UTC would shift it for
        // anyone east of Greenwich (CLAUDE.md §19).
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })();
      const clockOutFull = `${clockOutDay}T${formData.clock_out}:00`;
      const breakMinutes = formData.break_duration || 0;

      // Resolve employee_id - must be actual employee record ID, not business user ID
      // FIX: Use user.id (auth.users.id) for employee lookup
      let employeeId = formData.employee_id;
      if (!employeeId) {
        // Look up employee record using user.id (auth.users.id)
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (employeeError || !employeeData) {
          console.error('Could not find employee record for user.id:', user.id, employeeError);
          showToast('error', 'Could not identify employee. Please select an employee from the dropdown.');
          return;
        }
        employeeId = employeeData.id;
      }

      const timesheetData = {
        employee_id: employeeId,
        work_order_id: formData.job_id || null,
        date: formData.work_date, // Schema uses date, not work_date
        // #3349: the clock the user typed is COMPANY-local — that is what every list on this
        // page shows them, via fmtTime. fromDateTimeLocalInput read it as browser-local, so a
        // user outside the company's zone stored an entry offset by the difference.
        clock_in: companyWallClockToUtc(formData.work_date, formData.clock_in),
        clock_out: companyWallClockToUtc(clockOutDay, formData.clock_out),
        break_duration: breakMinutes, // Schema uses break_duration
        regular_hours: formData.regular_hours || 0,
        overtime_hours: formData.overtime_hours || 0,
        notes: formData.notes || '',
        company_id: user.company_id, // Required for RLS policies
        user_id: user.id, // Add user_id for tracking who created it
        // NEW Phase 1 fields
        time_type: formData.time_type || 'job',
        source: formData.source || 'manual',
        duration_minutes: calculateDuration(clockInFull, clockOutFull, breakMinutes),
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Only add status fields if we're implementing the approval workflow
      // This allows the feature to work even if the database hasn't been updated yet
      if (submitForApproval) {
        timesheetData.status = 'submitted';
        timesheetData.submitted_at = new Date().toISOString();
      } else {
        timesheetData.status = 'draft';
      }

      if (!(await confirmNoOverlap(employeeId, timesheetData.clock_in, timesheetData.clock_out))) return;

      // SECURITY: Use Supabase client for creating timesheet
      const { data, error } = await supabase
        .from('employee_timesheets')
        .insert(timesheetData)
        .select();

      if (error) {
        console.error('❌ Error creating timesheet:', error);
        console.error('❌ Timesheet data attempted:', timesheetData);
        throw error;
      }

      console.log('✅ Timesheet created successfully:', data);
      showToast('success', 'Time entry created successfully');
      setShowCreateModal(false);
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    } catch (error) {
      console.error('Error creating timesheet:', error);
      // Say what the server said. A bare "Failed to create time entry" gives the user nothing
      // to act on and hides which value was refused (board #899).
      showToast('error', `Failed to create time entry: ${error?.message || 'please check the times and try again.'}`);
    }
  };

  const handleUpdateTimesheet = async (formData) => {
    try {
      // NEW: Check if timesheet is locked
      const isLocked = await TimesheetEditHistoryService.isTimesheetLocked(
        selectedTimesheet.id
      );
      if (isLocked) {
        showToast('error', 'This time entry is locked and cannot be edited');
        return;
      }

      // Board #899. Both timestamps were built from the SAME work_date, so a shift ending
      // earlier than it starts — 22:00 to 06:00, an ordinary overnight call-out — persisted a
      // clock_out BEFORE its clock_in. The modal's own preview already treats that as the
      // next day (calculateTimesheetHours wraps), so the hours shown and the timestamps
      // stored disagreed, and whatever the database made of the reversed pair surfaced as a
      // generic "Failed to create time entry". The date has to wrap with the time.
      const clockInFull = `${formData.work_date}T${formData.clock_in}:00`;
      const clockOutDay = (() => {
        if (formData.clock_out > formData.clock_in) return formData.work_date;
        const d = new Date(`${formData.work_date}T00:00:00`);
        d.setDate(d.getDate() + 1);
        // Local parts, not toISOString — this is a calendar day, and UTC would shift it for
        // anyone east of Greenwich (CLAUDE.md §19).
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })();
      const clockOutFull = `${clockOutDay}T${formData.clock_out}:00`;
      const breakMinutes = formData.break_duration || 0;

      const timesheetData = {
        work_order_id: formData.job_id || null,
        date: formData.work_date,
        // #3349: company-local wall clock, matching the read in TimesheetFormModal and the
        // times the list shows. See the note on the create path above.
        clock_in: companyWallClockToUtc(formData.work_date, formData.clock_in),
        clock_out: companyWallClockToUtc(clockOutDay, formData.clock_out),
        break_duration: breakMinutes,
        regular_hours: formData.regular_hours,
        overtime_hours: formData.overtime_hours,
        notes: formData.notes,
        // NEW Phase 1 fields
        time_type: formData.time_type || selectedTimesheet.time_type || 'job',
        source: formData.source || selectedTimesheet.source || 'manual',
        duration_minutes: calculateDuration(clockInFull, clockOutFull, breakMinutes),
        edit_reason: formData.edit_reason,
        updated_at: new Date().toISOString()
      };

      // SECURITY: Use Supabase client for updating timesheet.
      // #3393: scoped to the company, locked on the updated_at this dialog was opened with, and
      // required to touch a row. An update that matches nothing is not an error to PostgREST,
      // so a row edited by someone else (or removed) in the meantime used to report "updated"
      // and log an edit that never happened.
      if (!(await confirmNoOverlap(selectedTimesheet.employee_id, timesheetData.clock_in, timesheetData.clock_out, selectedTimesheet.id))) return;

      let updateQuery = supabase
        .from('employee_timesheets')
        .update(timesheetData)
        .eq('id', selectedTimesheet.id)
        .eq('company_id', user.company_id);
      if (selectedTimesheet.updated_at) {
        updateQuery = updateQuery.eq('updated_at', selectedTimesheet.updated_at);
      }
      const { data: updatedRows, error } = await updateQuery.select('id');

      if (error) {
        console.error('Error updating timesheet:', error);
        throw error;
      }
      if (!updatedRows || updatedRows.length === 0) {
        showToast('error', 'This time entry was changed or removed by someone else. Nothing was saved — close and reopen it to see the latest version.');
        queryClient.invalidateQueries({ queryKey: ["timesheets"] });
        return;
      }

      // NEW: Log the edit to history
      await TimesheetEditHistoryService.logTimesheetEdit(
        selectedTimesheet.id,
        selectedTimesheet,
        timesheetData,
        formData.edit_reason || 'Manual edit',
        user.id,
        user.company_id
      );

      showToast('success', 'Time entry updated and edit logged');
      setShowEditModal(false);
      setSelectedTimesheet(null);
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    } catch (error) {
      console.error('Error updating timesheet:', error);
      showToast('error', 'Failed to update time entry');
    }
  };

  const handleDeleteTimesheet = async (timesheet) => {
    const ok = await confirm({
      title: 'Delete time entry?',
      message: 'Are you sure you want to delete this time entry?',
      confirmText: 'Delete',
      isDangerous: true
    });
    if (ok !== 'confirm') return;

    try {
      // SECURITY: Verify ownership or admin before delete
      if (timesheet.user_id !== user.id && !isAdmin(user)) {
        showToast('error', 'You can only delete your own time entries');
        return;
      }

      // FIXED: Cascade delete - remove related edit history records first
      const { error: historyError } = await supabase
        .from('timesheet_edit_history')
        .delete()
        .eq('timesheet_id', timesheet.id);

      if (historyError) {
        console.error('Error deleting timesheet history:', historyError);
        // Continue with main delete even if history deletion fails
      }

      // SECURITY: Use Supabase client for deleting timesheet
      const { error } = await supabase
        .from('employee_timesheets')
        .delete()
        .eq('id', timesheet.id);

      if (error) {
        console.error('Error deleting timesheet:', error);
        throw error;
      }

      showToast('success', 'Time entry deleted successfully');
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      showToast('error', 'Failed to delete time entry');
    }
  };

  const openEditModal = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setShowEditModal(true);
  };

  const handleSubmitForApproval = async (timesheet) => {
    try {
      const submittedAt = new Date().toISOString();

      // SECURITY: Use Supabase client for submitting timesheet
      const { error } = await supabase
        .from('employee_timesheets')
        .update({
          status: 'submitted',
          submitted_at: submittedAt,
          updated_at: submittedAt
        })
        .eq('id', timesheet.id);

      if (error) {
        console.error('Error submitting timesheet:', error);
        throw error;
      }

      // NEW: Log the submission to history
      await TimesheetEditHistoryService.logTimesheetEdit(
        timesheet.id,
        { status: timesheet.status },
        { status: 'submitted', submitted_at: submittedAt },
        'Marked as logged',
        user.id,
        user.company_id
      );

      showToast('success', 'Time entry logged');
      queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    } catch (error) {
      console.error('Error submitting timesheet:', error);
      if (error.message && error.message.includes('column "status" does not exist')) {
        showToast('warning', 'Approval workflow not yet configured. Please run the database migration first.');
      } else {
        showToast('error', 'Failed to submit time entry');
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return fmtTime(timestamp);
  };

  // #2878: an overnight shift's clock-out is on the NEXT day, but only times are shown, so
  // "Clock In 12:00 PM / Clock Out 11:00 AM" read as out-before-in. Stored data is correct
  // (0 rows have clock_out < clock_in); say which day it is. Company-timezone days.
  const endsNextDay = (ts) => Boolean(ts?.clock_in && ts?.clock_out
    && companyDateKey(ts.clock_out) > companyDateKey(ts.clock_in));

  const formatDuration = (hours) => {
    if (!hours) return '0h 0m';
    // ✅ FIX: Convert to total minutes first to avoid "60m" bug
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  };

  /**
   * Board #457, finishing the P29 removal on this screen.
   *
   * There is no approval step — "we record hours against a work order; nobody signs off on
   * them". But `submitted` was still rendered as amber "Pending", so every logged entry looked
   * like it was queued behind a decision that does not exist and never arrives. Jerry's two
   * rows had sat that way since they were entered.
   *
   * `submitted` now reads LOGGED, which is what it means: the entry is finished. `draft` stays,
   * because an unfinished entry is a real distinction a user wants.
   *
   * The legacy states are kept and NOT quietly folded into Logged, because they exist on live
   * rows — measured 2026-09-17: submitted 7, draft 4, approved 4, rejected 1. An `approved`
   * row IS just logged time, so it reads the same. A `rejected` one is not, and relabelling it
   * would misstate what somebody recorded; it keeps its own muted badge instead. Nothing can
   * create either state any more. The columns stay per the brief — dropping them is a
   * destructive schema decision of its own.
   */
  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: PencilIcon, label: 'Draft' },
      submitted: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Logged' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Logged' },
      rejected: { color: 'bg-gray-100 text-gray-500', icon: XCircleIcon, label: 'Rejected (legacy)' }
    };

    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  /** Board #457: the one place "Logged" is defined, matching what the badge shows. */
  const LOGGED_STATUSES = ['submitted', 'approved'];

  const getFilteredTimesheets = () => {
    let filtered = timesheets;

    if (statusFilter === 'logged') {
      filtered = filtered.filter(timesheet => LOGGED_STATUSES.includes(timesheet.status));
    }

    return filtered;
  };

  const filteredTimesheets = getFilteredTimesheets();
  // The JOB cell's text. #6163: "Sort by Job" now orders by this, the value the column shows;
  // it sorted by the raw number/title, so unlinked rows (Travel, No Job) all tied on ''.
  const jobCellLabel = (timesheet) => {
    const timeTypeConfig = TIME_TYPE_CONFIG[timesheet.time_type];
    const timeTypeLabel = timeTypeConfig?.label || timesheet.time_type || 'Time Entry';

    if (timesheet.work_order_number) {
      // Has a job - show job number
      if (timesheet.work_order_number.startsWith('QT-')) {
        return `Quote #${timesheet.work_order_number}`;
      } else if (timesheet.work_order_number.startsWith('WO-')) {
        return `WO #${timesheet.work_order_number}`;
      } else {
        return `#${timesheet.work_order_number}`;
      }
    } else if (timesheet.work_order_id) {
      // #4902: linked to a job that has no number (recurring-spawned
      // visits have NULL work_order_number) - not "No Job".
      return timesheet.job_title || 'Job';
    } else if (timesheet.time_type === 'job') {
      // Job type but no job selected - this is an error case
      return 'No Job';
    } else {
      // Non-job time type - show the time type label
      return timeTypeLabel;
    }
  };
  const tsSortValue = (t, key) => {
    switch (key) {
      case 'employee': return (t.employees?.full_name || t.employee_name || '').toLowerCase();
      case 'job': return jobCellLabel(t).toLowerCase();
      case 'date': return t.work_date || t.date || '';
      case 'clock_in': return t.clock_in || '';
      case 'status': return t.status || '';
      default: return Number(t[key]) || 0;
    }
  };
  const sortedTimesheets = !tsSort ? filteredTimesheets : [...filteredTimesheets].sort((a, b) => {
    const A = tsSortValue(a, tsSort.key); const B = tsSortValue(b, tsSort.key);
    const cmp = typeof A === 'number' ? A - B : String(A).localeCompare(String(B));
    return tsSort.direction === 'asc' ? cmp : -cmp;
  });

  // ✅ BACKEND RPC: Use backend-calculated hours directly.
  // #2624: "Hours Logged" counts the rows this page LABELS Logged (submitted/approved, see the
  // badge map above; LOGGED_STATUSES, board #457) — not drafts or rejected entries shown
  // alongside them under All Statuses.
  const totalHours = filteredTimesheets.reduce((sum, timesheet) => {
    return LOGGED_STATUSES.includes(timesheet.status) ? sum + (timesheet.total_hours || 0) : sum;
  }, 0);

  // Same rule as Hours Logged above: a draft's overtime is not logged overtime either. The tile
  // summed every filtered row, so one draft with 2.5 OT showed beside 0.0h logged (board #5465).
  const totalOvertimeHours = filteredTimesheets.reduce((sum, timesheet) => {
    return LOGGED_STATUSES.includes(timesheet.status) ? sum + (timesheet.overtime_hours || 0) : sum;
  }, 0);

  // #5955/#5956: the tiles count logged time only (above); say how much draft time in view they
  // leave out, so the gap to the table's own column totals is explained on the tile itself.
  const draftHoursInView = filteredTimesheets.reduce((sum, t) => (
    LOGGED_STATUSES.includes(t.status) ? sum : sum + (t.total_hours || 0)), 0);
  const draftOvertimeInView = filteredTimesheets.reduce((sum, t) => (
    LOGGED_STATUSES.includes(t.status) ? sum : sum + (t.overtime_hours || 0)), 0);

  const uniqueEmployeesInTimesheets = new Set(
    filteredTimesheets.map(t => t.employee_id)
  ).size;
  // Show actual employee count (not just those with timesheets)
  const uniqueEmployees = employees.length > 0 ? employees.length : uniqueEmployeesInTimesheets;

  const stats = calculateSummaryStats(filteredTimesheets);

  return (
    <div className="space-y-8 fade-in">
      {/* Modern Page Header.
          Header stats dropped entirely 2026-08-29 (stats={[]} below). This band rendered
          Total Hours / Employees / Pending while the tile row ~40px beneath it rendered
          the SAME three values as This Week / Active Team / Pending — three facts, twice
          each, and the header copies are not even clickable (ModernPageHeader disables a
          stat with no onClick). 'Pending' is gone per PRODUCT_DECISIONS P29, see the tile
          row; hours survive as the 'Hours Logged' tile, the one place they now appear. */}
      <ModernPageHeader
        compact
        title={user?.role === 'EMPLOYEE' ? 'My Time Tracking' : 'Team Time Tracking'}
        subtitle={user?.role === 'EMPLOYEE' ? 'Track your hours against jobs' : 'Manage team time tracking'}
        icon={ClockIcon}
        gradient="green"
        stats={[]}
        actions={[
          {
            // Board #455. 678c0e531 retitled the modal, its button, the empty state and the
            // toasts to "Add Time Off"; this page-header action was the one caller it did not
            // reach, so the top of the page still said "Request PTO". Per P29 there is no
            // approver — time off is a declaration that blocks scheduling immediately — so
            // "request" describes a step that does not exist.
            label: 'Add Time Off',
            icon: CalendarDaysIcon,
            onClick: () => setShowPTOModal(true)
          },
          {
            label: 'Log Time',
            icon: PlusIcon,
            onClick: () => setShowCreateModal(true)
          }
        ]}
      />

      {/* Time Tracking Analytics Cards - only show on timesheets/approvals tabs */}
      {activeTab !== 'pto' && <>
        {/* Was "This Week", which it was not. `totalHours` sums filteredTimesheets, and
            quickDateFilter defaults to 'all' (:52), so on first paint this rendered
            ALL-TIME hours under a weekly label — 68.5h spanning 2026-07-13..2026-08-24 on
            c8c96c4b. Its onClick also only set the tab it was already on, so it filtered
            nothing. The number is genuinely useful; it just has to say what it is. It now
            follows the date-range control the user actually sets, and names itself
            accordingly. (Not re-derived as a real week here: applyQuickDateFilter builds
            its boundaries with toISOString().split('T')[0], i.e. browser-UTC rather than
            company_today() — CLAUDE.md §19. Fixing that is a separate date-correctness
            job, and inventing a second week definition next to a wrong one is how two
            answers to one question happen.) */}
        <OperationalMetricStrip
          items={[
            {
              label: 'Hours Logged',
              value: `${totalHours.toFixed(1)}h`,
              icon: ClockIcon,
              tone: 'blue',
              detail: draftHoursInView > 0 ? `Excludes ${draftHoursInView.toFixed(1)}h not yet submitted` : 'Submitted time',
            },
            {
              label: 'Overtime',
              value: `${totalOvertimeHours.toFixed(1)}h`,
              detail: draftOvertimeInView > 0 ? `Excludes ${draftOvertimeInView.toFixed(1)}h not yet submitted` : undefined,
              icon: ArrowTrendingUpIcon,
              tone: 'orange',
              active: overtimeFilter,
              // Narrows to overtime WITHIN the current filters — the number was computed under
              // them. Clearing them turned one employee's 13.5h into the company's 116.5h, and on
              // /my/time it swapped your own hours for the team's (audit 2026-10-17).
              onClick: () => setOvertimeFilter(!overtimeFilter),
            },
          ]}
        />

        {/* 'Pending' REMOVED 2026-08-29 per PRODUCT_DECISIONS.md P29, quoted:
            "NO TIMESHEET APPROVAL. There is nothing to approve... Do NOT build or 'fix'
            approve/reject UI on /timesheets." It counted status='submitted' and called it
            "pending approvals". The bulk approve/reject buttons were already removed under
            that ruling (:1219-1222); this tile and the matching header stat were missed in
            the same pass, so the page kept advertising a queue the operator abolished —
            which is exactly the re-derivation P29 says sessions keep making.

            'Active Team' REMOVED — it navigated to /employees, i.e. the tile conceding it
            belongs on another page (the Customers 'Total Revenue' tell), and it was not
            "active" anyway: uniqueEmployees is employees.length (:925) over a fetch with
            no status filter, so it counted inactive staff too. Headcount is the Employees
            page's job. */}
      </>}

      {/* Alert */}
      {alert.show && (
        <div className={`p-4 rounded-md ${
          alert.type === 'success' ? 'bg-green-100 text-green-700' :
          alert.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
          alert.type === 'info' ? 'bg-blue-100 text-blue-700' :
          'bg-red-100 text-red-700'
        }`}>
          {alert.message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('timesheets')}
              className={`${
                activeTab === 'timesheets'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <ClockIcon className="w-4 h-4" />
              {user?.role === 'EMPLOYEE' ? 'My Time Tracking' : 'All Time Tracking'}
            </button>

            {/* Personal PTO Tab - For all users */}
            <button
              onClick={() => setActiveTab('pto')}
              className={`${
                activeTab === 'pto'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <CalendarDaysIcon className="w-4 h-4" />
              My Time Off
              {ptoRequests.filter(p => p.status === 'PENDING').length > 0 && (
                <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                  {ptoRequests.filter(p => p.status === 'PENDING').length}
                </span>
              )}
            </button>

            {/* Board #457: the "Pending Approvals" tab and its red count badge are gone, along
                with the approved/rejected notification chips below them. P29 is explicit —
                "NO TIMESHEET APPROVAL. There is nothing to approve" — so a queue counting
                entries waiting for a decision counted something that never happens, and the
                badge could only ever grow. Owners and admins keep full add/edit access to
                anyone's time through the normal list; nothing about what they can DO changed. */}
          </nav>
        </div>
      </div>



      {/* Board #1940. The analytics cards above (activeTab !== 'pto') and the table below
          (same guard) both hide on My Time Off — but everything BETWEEN them did not, so the
          PTO tab still showed the timesheet date filters, employee/job/status filters, the
          bulk-selection bar and the timesheet export row, all of them acting on a table that
          was not on screen. Three sections that belong to time tracking, now under the same
          guard as the two they sit between. */}
      {activeTab !== 'pto' && (<>
      {/* Filter Bar */}
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 p-4">
        {/* Quick Date Filters Row */}
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">Date:</span>
          {[
            { label: 'Today', value: 'today' },
            { label: 'This Week', value: 'thisWeek' },
            { label: 'Last Week', value: 'lastWeek' },
            { label: 'This Month', value: 'thisMonth' },
            { label: 'All', value: 'all' }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => applyQuickDateFilter(filter.value)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                quickDateFilter === filter.value
                  ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 border-primary-300 dark:border-primary-700'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-slate-600 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              {filter.label}
            </button>
          ))}
          {/* Custom date range inline */}
          <span className="text-sm text-gray-600 dark:text-slate-300 mx-2">or</span>
          <input
            type="date"
            value={dateRangeFilter.start}
            onChange={(e) => { setDateRangeFilter({ ...dateRangeFilter, start: e.target.value }); setQuickDateFilter(''); }}
            className="px-2 py-1 text-sm rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <span className="text-gray-400">–</span>
          <input
            type="date"
            value={dateRangeFilter.end}
            onChange={(e) => { setDateRangeFilter({ ...dateRangeFilter, end: e.target.value }); setQuickDateFilter(''); }}
            className="px-2 py-1 text-sm rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Filters Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Employee Filter - Only show for admins/owners, and never on /my/time, where the
              whole point is that the page is about one person. */}
          {!scopeToSelf && user?.role !== 'EMPLOYEE' && (
            <select
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Employees</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>{employee.full_name}</option>
              ))}
            </select>
          )}

          {/* Job Filter */}
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Jobs</option>
            {jobs.map(job => (
              <option key={job.id} value={job.id}>{job.displayLabel || job.job_title}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {/* Board #457: Draft vs Logged is the only real distinction under P29. Approved
                and Rejected are gone as CHOICES because nothing can reach them any more;
                legacy rows in those states still appear under All Statuses, so nothing is
                hidden from anyone looking at their own history. */}
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="logged">Logged</option>
          </select>
        </div>

        {/* Footer: Count + Actions */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200/50 dark:border-slate-700/50">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {filteredTimesheets.length > 0 ? `${filteredTimesheets.length} timesheet${filteredTimesheets.length !== 1 ? 's' : ''}` : ''}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setSearchTerm('');
                setEmployeeFilter('all');
                setJobFilter('all');
                setStatusFilter('all');
                setOvertimeFilter(false);
                setDateRangeFilter({ start: '', end: '' });
                setQuickDateFilter('all');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {(user?.role?.toLowerCase() === 'owner' || user?.role?.toLowerCase() === 'admin') && selectedIds.size > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-primary-700">
              {selectedIds.size} timesheet{selectedIds.size > 1 ? 's' : ''} selected
            </div>
            <div className="flex items-center gap-2">
              {/* Bulk Approve / Bulk Reject REMOVED 2026-08-16 (operator).
                  Same ruling as the row buttons: PRODUCT_DECISIONS P29, time
                  TRACKING only - there is no approval step to perform in bulk
                  either. Selection is kept for the remaining bulk actions. */}
              <button
                onClick={() => setSelectedIds(new Set())}
                className="btn-secondary"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export & Summary */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          {getFilteredTimesheets().length} timesheet{getFilteredTimesheets().length !== 1 ? 's' : ''} found
          {selectedIds.size > 0 && (
            <span className="ml-2 text-primary-600">({selectedIds.size} selected)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Enhanced Export Options - Phase 2 */}
          <div className="relative inline-block text-left">
            <select
              onChange={(e) => {
                const [format, type] = e.target.value.split('-');
                const data = type === 'selected'
                  ? getFilteredTimesheets().filter(t => selectedIds.has(t.id))
                  : getFilteredTimesheets();

                if (format === 'csv') {
                  exportTimesheetsCSV(data);
                } else if (format === 'json') {
                  exportTimesheetsJSON(data);
                }
                e.target.value = ''; // Reset selection
              }}
              className="btn-secondary flex items-center gap-2 pr-8"
              defaultValue=""
            >
              <option value="" disabled>Export Options</option>
              <option value="csv-all">📊 Export CSV (All Filtered)</option>
              <option value="json-all">📋 Export JSON (All Filtered)</option>
              {selectedIds.size > 0 && (
                <>
                  <option value="csv-selected">📊 Export CSV (Selected)</option>
                  <option value="json-selected">📋 Export JSON (Selected)</option>
                </>
              )}
            </select>
          </div>
        </div>
      </div>
      </>)}

      {/* Timesheets Table - Hide when PTO tab is active */}
      {activeTab !== 'pto' && (
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-slate-800">
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all timesheets"
                    title="Select all timesheets"
                    checked={selectedIds.size > 0 && selectedIds.size === getFilteredTimesheets().length}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                {user?.role !== ROLES.EMPLOYEE && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <SortableHeader sortKey="employee" sortConfig={tsSort} onSort={handleTsSort}>Employee</SortableHeader>
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortableHeader sortKey="job" sortConfig={tsSort} onSort={handleTsSort}>Job</SortableHeader>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortableHeader sortKey="date" sortConfig={tsSort} onSort={handleTsSort}>Work Date</SortableHeader>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortableHeader sortKey="clock_in" sortConfig={tsSort} onSort={handleTsSort}>Clock In</SortableHeader>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Clock Out
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortableHeader sortKey="total_hours" sortConfig={tsSort} onSort={handleTsSort}>Paid Hours</SortableHeader>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Lunch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortableHeader sortKey="overtime_hours" sortConfig={tsSort} onSort={handleTsSort}>Overtime</SortableHeader>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <SortableHeader sortKey="status" sortConfig={tsSort} onSort={handleTsSort}>Status</SortableHeader>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={user?.role !== ROLES.EMPLOYEE ? "12" : "11"} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Loading time entries...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredTimesheets.length === 0 ? (
                <tr>
                  <td colSpan={user?.role !== ROLES.EMPLOYEE ? "12" : "11"} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="bg-gray-100 dark:bg-slate-800 rounded-full p-4 mb-4">
                        <ClockIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      {/* Board #457: the approvals branch is unreachable now that the tab is
                          gone, and "All time entries have been reviewed" described a review
                          nobody performs. */}
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No Time Entries Found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                        Try adjusting your filters or create a new time entry.
                      </p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn-primary flex items-center gap-2"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Create Timesheet
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedTimesheets.map((timesheet) => {
                  const totalHours = timesheet.total_hours || 0;

                  return (
                    <tr key={timesheet.id} className={`hover:bg-gray-50 dark:hover:bg-slate-800 ${selectedIds.has(timesheet.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          aria-label={`Select timesheet for ${timesheet.employee_name || timesheet.work_date || ''}`.trim()}
                          checked={selectedIds.has(timesheet.id)}
                          onChange={() => toggleRow(timesheet.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      {user?.role !== 'EMPLOYEE' && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                timesheet.user_id === user.id
                                  ? 'bg-primary-100 dark:bg-primary-900'
                                  : 'bg-gray-100 dark:bg-slate-700'
                              }`}>
                                <span className={`text-sm font-medium ${
                                  timesheet.user_id === user.id
                                    ? 'text-primary-700 dark:text-primary-300'
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  {timesheet.employees?.full_name?.charAt(0) || 'U'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {timesheet.employees?.full_name || 'Unknown Employee'}
                                </span>
                                {(timesheet.employee_id === currentEmployee?.id || timesheet.employees?.user_id === user.id) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                    You
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BriefcaseIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {/* FIXED: Smart work order number display - shows job number or time type label */}
                              {jobCellLabel(timesheet)}
                            </div>
                            {timesheet.job_title ? (
                              // Has job title - show it
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {timesheet.job_title}
                              </div>
                            ) : timesheet.time_type !== 'job' ? (
                              // No job title, non-job time type - show description
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {TIME_TYPE_CONFIG[timesheet.time_type]?.description || ''}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CalendarDaysIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {/* #5583: the company's Date Format, as My Time Off below already
                                  uses; toLocaleDateString() followed the browser instead. */}
                              {fmtDateOnly(timesheet.date, DATE_NUMERIC)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {fmtDateOnly(timesheet.date, { weekday: 'short' })}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 text-green-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatTime(timesheet.clock_in)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 text-red-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatTime(timesheet.clock_out)}
                            {endsNextDay(timesheet) && (
                              <span className="ml-1 text-xs font-normal text-amber-700 dark:text-amber-400" title="Clocked out the next day">+1 day</span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {timesheet.total_paid_hours ? `${timesheet.total_paid_hours.toFixed(1)}h` : formatDuration(totalHours)}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {/* #5958: hours and minutes, the same unit as the total above it. One
                                decimal hour rounded 1 minute of overtime to "0.0h", so "8h 1m"
                                sat over "8.0h reg + 0.0h OT", which does not add up. */}
                            {timesheet.regular_hours && timesheet.overtime_hours ?
                              `${formatDuration(timesheet.regular_hours)} reg + ${formatDuration(timesheet.overtime_hours)} OT` :
                              'Total hours'
                            }
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {timesheet.break_duration > 0 ?
                          <span className="text-gray-600 dark:text-gray-400">
                            {timesheet.break_duration}m break
                          </span> :
                          <span className="text-gray-400 dark:text-gray-500">-</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {timesheet.overtime_hours && timesheet.overtime_hours > 0 ?
                          <span className="text-amber-700 dark:text-amber-400 font-medium">{formatDuration(timesheet.overtime_hours)}</span> :
                          '-'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(timesheet.status || 'draft')}
                          {getStatusNotification(timesheet) && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              getStatusNotification(timesheet).type === 'approved'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`} title={getStatusNotification(timesheet).message}>
                              {getStatusNotification(timesheet).icon}
                            </span>
                          )}
                        </div>
                        {timesheet.status === 'approved' && timesheet.approved_by_user && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            by {timesheet.approved_by_user.full_name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {timesheet.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {/* Employee actions - only for their own timesheets */}
                          {user.role?.toLowerCase() === 'employee' && (
                            timesheet.employee_id === currentEmployee?.id ||
                            timesheet.employees?.user_id === user.id
                          ) && (
                            <>
                              {(timesheet.status === 'draft' || timesheet.status === 'rejected') && (
                                <>
                                  <button
                                    onClick={() => openEditModal(timesheet)}
                                    className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 p-1 rounded flex items-center gap-1"
                                    title="Edit time entry"
                                  >
                                    <PencilIcon className="w-4 h-4" />
                                    <span>Edit</span>
                                  </button>
                                  {/* Board #457: this said "Submit for approval" and promised a
                                      review that P29 says does not exist. It is the draft ->
                                      logged transition, so it says that. The handler name is
                                      left alone; renaming it is churn with no user effect. */}
                                  <button
                                    onClick={() => handleSubmitForApproval(timesheet)}
                                    className="text-green-600 hover:text-green-900 p-1 rounded flex items-center gap-1"
                                    title="Mark this entry as logged"
                                  >
                                    <DocumentCheckIcon className="w-4 h-4" />
                                    <span>Mark logged</span>
                                  </button>
                                </>
                              )}
                              {timesheet.status === 'draft' && (
                                <button
                                  onClick={() => handleDeleteTimesheet(timesheet)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded flex items-center gap-1"
                                  title="Delete time entry"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                  <span>Delete</span>
                                </button>
                              )}
                            </>
                          )}

                          {/* Admin/Owner actions - can manage all timesheets */}
                          {(user?.role?.toLowerCase() === 'owner' || user?.role?.toLowerCase() === 'admin') && (
                            <>
                              {/* Approve / Reject REMOVED 2026-08-16 (operator).
                                  PRODUCT_DECISIONS P29: this is time TRACKING,
                                  not payroll - nobody signs off on hours, so
                                  there is nothing to approve or deny. The row
                                  keeps Edit. */}

                              {/* Edit action for any timesheet that's not approved (admins can fix issues) */}
                              {timesheet.status !== 'approved' && (
                                <button
                                  onClick={() => openEditModal(timesheet)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 p-1 rounded flex items-center gap-1"
                                  title="Edit time entry"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                  <span>Edit</span>
                                </button>
                              )}

                              {/* #6027: owners/admins can remove a logged entry too — P29 says there is no
                                  approval step, so "logged" is not a lock, and a mistaken entry otherwise
                                  stayed in labor cost for good. The delete RLS already allows it. */}
                              {timesheet.status !== 'approved' && (
                                <button
                                  onClick={() => handleDeleteTimesheet(timesheet)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded flex items-center gap-1"
                                  title="Delete time entry"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                  <span>Delete</span>
                                </button>
                              )}

                              {/* Special admin override for approved timesheets */}
                              {timesheet.status === 'approved' && (
                                <button
                                  onClick={() => openEditModal(timesheet)}
                                  className="text-orange-600 hover:text-orange-900 p-1 rounded flex items-center gap-1"
                                  title="Admin override - Edit approved timesheet"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                  <span>Edit</span>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* My PTO Table - Show when PTO tab is active */}
      {activeTab === 'pto' && (
        <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg border border-blue-200/50 dark:border-blue-900/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">My Time Off</h3>
          </div>
          <div className="table-scroll">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduling
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ptoRequests.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="bg-gray-100 rounded-full p-4 mb-4">
                          <CalendarDaysIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Time Off</h3>
                        <p className="text-gray-500 text-sm mb-4">
                          Add time off to block those dates from scheduling. It takes effect immediately — there is no approval step.
                        </p>
                        <button
                          onClick={() => setShowPTOModal(true)}
                          className="btn-primary flex items-center gap-2"
                        >
                          <CalendarDaysIcon className="w-4 h-4" />
                          Add Time Off
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  ptoRequests.map((pto) => {
                    // Until business hours load, the stored figure; then the live rule.
                    const daysCount = companyBusinessHours === undefined
                      ? Math.round((pto.hours_requested || 0) / 8 * 10) / 10
                      : timeOffDays(pto.starts_at, pto.ends_at, { businessHours: companyBusinessHours });

                    // Format dates safely using local time to avoid timezone shifts
                    // Extract date part directly from ISO string to avoid JS date parsing issues
                    // starts_at/ends_at hold the calendar day at UTC midnight, so the date part is
                    // the day. It was hardcoded MM/DD/YYYY beside a Submitted column in the
                    // company's own Date Format; fmtDateOnly + DATE_NUMERIC follows that setting.
                    const formatDateSafe = (dateStr) => (dateStr ? fmtDateOnly(dateStr, DATE_NUMERIC) || '-' : '-');

                    return (
                      <tr key={pto.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-lg mr-2">
                              {pto.kind === 'VACATION' ? '🏖️' :
                               pto.kind === 'SICK' ? '🤒' :
                               pto.kind === 'PERSONAL' ? '👤' :
                               pto.kind === 'BEREAVEMENT' ? '🕊️' :
                               pto.kind === 'JURY_DUTY' ? '⚖️' :
                               pto.kind === 'HOLIDAY' ? '🎉' :
                               pto.kind === 'UNPAID' ? '📝' :
                               pto.kind === 'FMLA' ? '⚕️' :
                               pto.kind === 'JURY' ? '⚖️' : '📝'}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {pto.kind.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateSafe(pto.starts_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateSafe(pto.ends_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {daysCount > 0 ? `${daysCount} day${daysCount !== 1 ? 's' : ''}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* Board #455. These badges said Pending / Approved / Rejected, which
                              describes an approval workflow that does not exist: migration
                              20260824a records the ruling that time off exists ONLY to block
                              scheduling, that there is no approver, and that PENDING and
                              APPROVED both block slots unless rejected or cancelled. So an
                              employee watched their own time off sit at "Pending" forever,
                              waiting on nobody.

                              The column is KEPT rather than removed, and re-pointed at the
                              fact that matters. Removing it would hide the one thing an
                              employee genuinely needs to know — a cancelled or rejected entry
                              does NOT block the schedule, and without this they could not
                              tell which of their entries are actually in force. */}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            pto.status === 'REJECTED' || pto.status === 'CANCELLED'
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {pto.status === 'REJECTED' || pto.status === 'CANCELLED'
                              ? 'Not blocking'
                              : '🚫 Unavailable'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {pto.note || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {fmtDate(pto.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {/* Edit/Delete actions for PENDING requests */}
                            {pto.status === 'PENDING' && (
                              <>
                                <button
                                  onClick={() => openPTEEditModal(pto)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 p-1 rounded flex items-center gap-1"
                                  title="Edit request"
                                >
                                  <PencilIcon className="w-4 h-4" />
                                  <span>Edit</span>
                                </button>
                                <button
                                  onClick={() => handlePTODelete(pto.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded flex items-center gap-1"
                                  title="Delete request"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                  <span>Delete</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}



      {/* Create Timesheet Modal */}
      <TimesheetFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateTimesheet}
        employees={employees}
        jobs={jobs}
      />

      {/* Edit Timesheet Modal */}
      <TimesheetFormModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTimesheet(null);
        }}
        onSubmit={handleUpdateTimesheet}
        timesheet={selectedTimesheet}
        employees={employees}
        jobs={jobs}
      />


      {/* PTO Request Modal - Now in the right place! */}
      <PTORequestModal
        isOpen={showPTOModal}
        onClose={() => setShowPTOModal(false)}
        onSubmit={handlePTOSubmit}
        user={user}
        employee={currentEmployee}
      />

      {/* PTO Edit Modal */}
      <PTOEditModal
        isOpen={showPTEEditModal}
        onClose={() => {
          setShowPTEEditModal(false);
          setSelectedPTEEdit(null);
        }}
        onSubmit={handlePTOUpdate}
        ptoRequest={selectedPTEEdit}
        employee={currentEmployee}
      />

      <ConfirmationModal isOpen={!!confirmDialog?.isOpen} {...(confirmDialog || {})} />
    </div>
  );
};

export default Timesheets;



